import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback error={this.state.error} onReset={this.handleReset} />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onReset }) => {
  const { t } = useTranslation();
  const noResultsMessage = t('search.no_results');
  const sentences = noResultsMessage.split(/\.\s+/);
  const headingText = sentences[0].endsWith('.')
    ? sentences[0]
    : `${sentences[0]}.`;
  const paragraphText = sentences.slice(1).join('. ');

  return (
    <div className="error-boundary-container">
      <div className="error-boundary-content">
        <h1>{headingText}</h1>
        {paragraphText && <p>{paragraphText}</p>}
        {error && (
          <details className="error-details">
            <summary>Details</summary>
            <pre>{error.toString()}</pre>
          </details>
        )}
        <button onClick={onReset} className="error-boundary-button">
          {t('search.button')}
        </button>
      </div>
    </div>
  );
};

export default ErrorBoundary;